import React, { useState, useEffect, useContext, useRef } from "react";
import MetaTags from "react-meta-tags";
import { LanguageContext } from "./../language-context";
import { PageTitle } from "./helper/PageTitle";
import { Scroll } from "./helper/Scroll";
import * as variables from "./helper/Variables";
import emailjs from "@emailjs/browser";
import styled, { keyframes } from "styled-components";

// Animacja opacity
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled.div`
  ${variables.Flex};
  ${variables.DimensionsView};
  background-color: ${variables.BACKGROUND_GRAY};

  @media (max-width: 1000px) {
    display: flex;
    align-items: center;
    flex-direction: column;
    height: auto;
  }
`;

const RightSide = styled.div`
  ${variables.Flex};
  flex: 1;

  @media (max-width: 1000px) {
    width: 100vw;
    padding: 2rem 0;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 80%;
  max-width: 600px;

  label {
    margin-top: 15px;
    margin-bottom: 5px;
    font-weight: bold;
  }

  input,
  textarea {
    padding: 10px;
    font-size: 1rem;
    border: none;
    resize: none;
    outline: none;
    box-shadow: none;
    border: 3px solid black;
    background-color: white;
    color: black;
  }

  input[type="submit"] {
    margin-top: 20px;
    background-color: ${variables.BLACK};
    color: ${variables.DIMGRAY};
    opacity: 0;
    animation: ${variables.ChangeOpacity} 0.5s 1.5s linear forwards;

    &:hover {
      color: ${variables.WHITE};
      cursor: pointer;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 1000px) {
      max-width: 600px;
    }
  }
`;

const Image = styled.img`
  ${variables.Flex};
  flex: 1;
  max-width: 45%;
  max-height: 80%;
  object-fit: contain;
  padding: 0 5%;
  opacity: 0;
  animation: ${variables.ChangeOpacity} 0.5s 1.5s linear forwards;

  @media (max-width: 1000px) {
    max-width: 90%;
    max-height: 80%;
    padding-top: 5rem;
  }
`;

const Address = styled.p`
  position: absolute;
  top: 57%;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${variables.TITLE_FONT};
  animation: ${variables.ChangeColor} 1.25s linear;

  @media (max-width: 500px) {
    font-size: ${variables.REGULAR_FONT};
    top: 60%;
  }
`;

const StatusMessage = styled.p`
  margin-top: 1.5rem;
  font-weight: bold;
  font-size: ${variables.REGULAR_FONT};
  text-align: center;
  animation: ${fadeIn} 0.5s ease-in;
  color: ${variables.DARK};
`;

export const Contact = () => {
  const usedLanguage = useContext(LanguageContext);
  const user = "kontakt";
  const domain = "michalkrol.com";
  const form = useRef();
  const statusRef = useRef();

  const {
    contactHeader,
    contactButton,
    contactData,
    contactEmail,
    contactMessage,
    contactErrorBackUp,
    contactErrorInvalidEmail,
    contactErrorSending,
    contactSuccsessSending,
  } = usedLanguage.language.contact;

  const popUpImageId = Object.keys(usedLanguage.language.artworks)[0];

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    emailjs.init("AKCxaF1zGm1D6mJsD");
  }, []);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const sendEmail = (e) => {
    e.preventDefault();

    setStatus("sending");
    setErrorMessage("");

    const formData = new FormData(form.current);
    const email = formData.get("clientEmail");

    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMessage(contactErrorInvalidEmail);
      statusRef.current?.focus();
      return;
    }

    emailjs
      .sendForm("service_8xaw19p", "template_cz3d1m3", form.current)
      .then(() => {
        setStatus("success");
        form.current.reset();
        statusRef.current?.focus();
      })
      .catch((error) => {
        setStatus("error");
        setErrorMessage(`${contactErrorBackUp} ${user}@${domain}.`);
        console.error(error);
        statusRef.current?.focus();
      });
  };

  Scroll();

  return (
    <main>
      <MetaTags>
        <title>{contactHeader} | Structuralist</title>
        <meta name="description" content={contactHeader} />
        <meta
          name="keywords"
          content={`"Structuralist", "Michał Król", ${contactHeader}`}
        />
      </MetaTags>

      <PageTitle
        text={contactHeader}
        backgroundColor={variables.BACKGROUND_GRAY}
      />

      <Address>
        {user}@{domain}
      </Address>

      <Wrapper>
        <Image
          src={`./images/artworks/${popUpImageId}/${popUpImageId}-1.jpg`}
          alt={`Latest painting, Michał Król - Structuralist`}
        />

        <RightSide>
          <StyledForm ref={form} onSubmit={sendEmail}>
            <label>{contactData}</label>
            <input type="text" name="clientName" required />

            <label>{contactEmail}</label>
            <input type="email" name="clientEmail" required />

            <label>{contactMessage}</label>
            <textarea name="clientMessage" rows="10" required />

            <input
              type="submit"
              value={status === "sending" ? contactErrorSending : contactButton}
              disabled={status === "sending"}
            />

            {status === "success" && (
              <StatusMessage status="success" ref={statusRef} tabIndex="-1">
                {contactSuccsessSending}
              </StatusMessage>
            )}
            {status === "error" && (
              <StatusMessage status="error" ref={statusRef} tabIndex="-1">
                {errorMessage}
              </StatusMessage>
            )}
          </StyledForm>
        </RightSide>
      </Wrapper>
    </main>
  );
};
